import { zodResolver } from '@hookform/resolvers/zod';
import { useEffect, useState } from 'react';
import { useForm, useFormContext, type Resolver } from 'react-hook-form';
import { z } from 'zod';
import { GeocodeResponse } from '../../../api/DTO/GeocodeResponse';
import {
    RefineSearchFilterFormValues,
    LanguageProficiencyLevelList,
    SearchPeriodList,
} from '../../../api/useSearchFilters';
import {
    maxYearsOfExperience,
    minLocationRadius,
    minYearsOfExperience,
    useIdentifiedListContext,
} from '../utils/IdentifiedListContext';
import ValidationErrors from '@/Utils/ValidationErrors';

export const oneFilterMinimumRule = 'oneFilterMinimumRule';

/** Form-stored chip rows only use `label` (not `group`, which is dropdown-only). */
const filtersAutocompleteField = z.object({
    label: z.string(),
});

export const refineSearchFiltersFormValidator = z
    .object({
        jobTitles: z.array(filtersAutocompleteField),
        jobTitleSearchPeriod: z.enum(SearchPeriodList),
        companies: z.array(filtersAutocompleteField),
        companiesSearchPeriod: z.enum(SearchPeriodList),
        locations: z.array(z.custom<GeocodeResponse>((value) => typeof value === 'object' && value !== null)),
        locationRadius: z.number().min(minLocationRadius).default(minLocationRadius),
        keywords: z.array(filtersAutocompleteField),
        yearsOfExperience: z.tuple([
            z.number().min(minYearsOfExperience).max(maxYearsOfExperience),
            z.number().min(minYearsOfExperience).max(maxYearsOfExperience),
        ]),
        includeEmptyExperience: z.boolean().default(false),
        languages: z.array(filtersAutocompleteField),
        languageProficiency: z.enum(LanguageProficiencyLevelList),
    })
    .superRefine(({ jobTitles, companies, locations, keywords }, context) => {
        const hasMinimumFilter =
            jobTitles.length > 0 || companies.length > 0 || locations.length > 0 || keywords.length > 0;

        if (!hasMinimumFilter) {
            context.addIssue({
                code: 'custom',
                message: ValidationErrors.AISearchOneMinimumFilterRequired,
                path: [oneFilterMinimumRule],
            });
        }
    });

export const useRefineSearchFiltersForm = () => {
    const { refineSearchFilters } = useIdentifiedListContext();
    const [disabled, setDisabled] = useState(false);
    const form = useForm<RefineSearchFilterFormValues>({
        mode: 'all',
        disabled,
        defaultValues: refineSearchFilters,
        resolver: zodResolver(refineSearchFiltersFormValidator) as Resolver<RefineSearchFilterFormValues>,
    });

    useEffect(() => setDisabled(form.formState.isSubmitting), [form.formState.isSubmitting]);

    return form;
};

export const useRefineSearchFiltersFormContext = () => useFormContext<RefineSearchFilterFormValues>();
